import type { RowData } from "@tanstack/react-table";
import { resolveColumnMeta } from "../../columns/columnKinds";
import type { DataGridCell } from "../../columns/types";
import type {
    DataGridActiveEditor,
    DataGridListEditingOptions,
} from "../../types";
import {
    getListReadOnlyReason,
    isLabelRowCell,
    isListCellEditable,
} from "../cellAccess";
import type { EditContext } from "../editActions";
import type { ListCellState } from "./listCellContext";
import {
    closeListEditor,
    closeListEditorAndCommit,
    openListEditor,
} from "./listEditActions";

/**
 * A cell's list editing state, or `null` when the table has no list editing,
 * the column has no `edit`, the cell is on a label row (`isLabelRowCell`),
 * or it holds a draft row's create input. `editor` is the row's open editor,
 * if any. Access follows `listEditing`, the rules of the render drawing the
 * cell, so a change to them redraws it.
 */
export const getListCellState = <TData extends RowData>({
    cell,
    listEditContext,
    listEditing,
    editor,
}: {
    cell: DataGridCell<TData>;
    listEditContext: EditContext<TData> | null;
    listEditing: DataGridListEditingOptions<TData> | undefined;
    editor: DataGridActiveEditor | null;
}): ListCellState | null => {
    const columnId = cell.column.id;
    if (
        listEditContext === null ||
        isLabelRowCell(listEditContext.instance, cell) ||
        !resolveColumnMeta(cell.column.columnDef, columnId).isListEditable
    ) {
        return null;
    }
    const { instance } = listEditContext;
    if (
        instance.rowKinds.draft?.columnId === columnId &&
        instance.rowKinds.isDraft(cell.row.original)
    ) {
        return null;
    }
    const address = { rowId: cell.row.id, columnId };
    const access = { instance, cell, listEditing };
    const isEditable = isListCellEditable(access);
    return {
        access: isEditable ? "editable" : "readOnly",
        readOnlyReason: isEditable ? undefined : getListReadOnlyReason(access),
        isOpen: isEditable && editor?.columnId === columnId,
        setOpen: (isOpen) => {
            if (isOpen) {
                openListEditor(listEditContext, address);
            } else {
                closeListEditor(listEditContext, address);
            }
        },
        closeAndCommit: (commit) =>
            closeListEditorAndCommit(listEditContext, { address, commit }),
    };
};
